import * as React from "react";
import { connect } from "react-redux";
import Link from "gatsby-link";
import * as Prism from "prismjs";
import { CopyToClipboard } from "react-copy-to-clipboard";
import * as Tooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap.css";

import WithHeaders from "../WithHeaders";

import { selectors as userSelectors } from "../../reducers/current_user";

// note: uncomment these if/when we support these languages
// require('prismjs/components/prism-markdown');
// require('prismjs/components/prism-markup');
// require('prismjs/components/prism-jsx');
// require('prismjs/components/prism-go');

import TryItIcon from "./TryItIcon";
import CopyIcon from "./CopyIcon";
import { LabExample, LabExampleHttp, LabExampleJs } from "../../reducers/lab";
import { createHttpExampleKey, createJsExampleKey } from "../../utils/example_key";
import { methodAllowsBody } from "../../reducers/lab_http";

/**
 * displays a pre tag that looks like a code block but does not
 * run through prism... displays "Intentionally left blank" text
 */
const IntentionallyBlank = () => (
	<pre
		style={{
			color: "#636363",
			backgroundColor: "#272822",
			paddingLeft: "1.61rem",
			paddingRight: "1.61rem",
			paddingBottom: "1em",
			paddingTop: "1em",
		}}
	>
		Body intentionally left blank.
	</pre>
);

interface ExampleProps {
	// from rehype
	language: string;
	tryit: string;
	copytoclipboard: string;
	heading: string;
	title: string;

	// injected
	apiurl: string;
	isLoggedIn: boolean;
	apiverb: string;
	authType: string;
	headers: HttpHeadersDict;
	openInLab(example: LabExample): void;
}

// todo: json, swift, java, http

/**
 * displays a request example and allows the user to copy it to
 * clipboard in various forms as well as perform the request and
 * view the response.
 */
class Example extends React.Component<ExampleProps, any> {
	state = {
		copiedToClipboard: false,
		responseBody: undefined,
	};

	constructor(props: ExampleProps) {
		super(props);
	}

	iconStyles = { height: "42px", width: "42px", margin: "auto" };
	timeout = null;

	get exampleText(): string {
		let rawCodeString: string = this.props.children[0] as string;

		// if the code string starts with a newline, remove that
		if (rawCodeString.length > 0 && rawCodeString.charAt(0) === "\n") {
			rawCodeString = rawCodeString.substring(1, rawCodeString.length);
		}

		// if the code string ends with a newline, remove that
		if (rawCodeString.length > 0 && rawCodeString.charAt(rawCodeString.length - 1) === "\n") {
			rawCodeString = rawCodeString.substring(0, rawCodeString.length - 1);
		}

		// note:
		// remove any lines with only a single space character
		// this is because our markdown docs cannot have newlines without
		// some content on each line for some reason, so we just use a
		// single space and strip it out here
		rawCodeString = rawCodeString
			.split("\n")
			.map(line => (line !== " " ? line : ""))
			.join("\n");

		return rawCodeString;
	}

	copyText() {
		clearTimeout(this.timeout);
		this.setState({ copiedToClipboard: true });
		this.timeout = setTimeout(() => this.setState({ copiedToClipboard: false }), 3000);
	}

	renderCopyToClipboard(value: string) {
		return (
			<Tooltip
				placement="top"
				overlay={<span>{this.state.copiedToClipboard ? "Copied" : "Copy"}</span>}
			>
				<CopyToClipboard text={value} onCopy={this.copyText.bind(this)}>
					<CopyIcon
						fillColor="rgba(0, 0, 0, 0.08)"
						iconColor="#FFFFFF"
						style={{ marginBottom: 0, cursor: "pointer" }}
					/>
				</CopyToClipboard>
			</Tooltip>
		);
	}

	openInLab() {
		this.props.openInLab(this.getAsExample());
	}

	getAsExample(): LabExample {
		// note: this is intentional (http examples have json language request bodies)
		if (this.props.language === "json") {
			return this.getAsHttpExample();
		} else if (this.props.language === "js") {
			return this.getAsJsExample();
		}

		throw new Error(`cannot open example of language "${this.props.language}"`);
	}

	getAsHttpExample(): LabExampleHttp {
		return {
			key: createHttpExampleKey(this.props.title, this.props.apiverb, this.props.apiurl),
			timestampIso: "",
			type: "http",
			meta: {
				docTitle: this.props.title,
				heading: this.props.heading || "",
			},
			data: {
				request: {
					url: this.props.apiurl,
					method: this.props.apiverb,
					body: this.exampleText,
					headers: this.props.headers,
					status: "",
					authType: this.props.authType,
					isBodyAllowed: methodAllowsBody(this.props.apiverb, this.exampleText),
				},
				response: undefined,
			},
		};
	}

	getAsJsExample(): LabExampleJs {
		return {
			key: createJsExampleKey(this.props.title, this.exampleText),
			timestampIso: "",
			type: "js",
			meta: {
				docTitle: this.props.title,
				heading: this.props.heading || "",
			},
			data: {
				code: this.exampleText,
				output: [],
				status: "",
			},
		};
	}

	render() {
		const { tryit, isLoggedIn } = this.props;
		let grammar;

		if (this.props.language === "json") {
			require("prismjs/components/prism-json");
			grammar = Prism.languages.json;
		} else if (this.props.language === "js" || this.props.language === "ts") {
			require("prismjs/components/prism-typescript");
			grammar = Prism.languages.typescript;
		} else if (this.props.language === "shell") {
			require("prismjs/components/prism-bash");
			grammar = Prism.languages.bash;
		} else if (this.props.language === "swift") {
			require("prismjs/components/prism-swift");
			grammar = Prism.languages.swift;
		} else if (this.props.language === "java") {
			require("prismjs/components/prism-java");
			grammar = Prism.languages.java;
		} else if (this.props.language === "http") {
			require("prismjs/components/prism-http");
			grammar = Prism.languages.http;
		} else {
			throw new Error(
				`language for example component not supported: ${JSON.stringify(this.props.language)}`
			);
		}

		const markup = Prism.highlight(this.exampleText, grammar);

		const canTry: boolean =
			tryit === "true" &&
			process.env.FEATURE_USER_ENABLED === "true" &&
			process.env.FEATURE_USER_LAB_ENABLED === "true";

		return (
			<div
				style={{
					marginBottom: "1.61em",
				}}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "row",
						backgroundColor: "#607D8B",
						paddingRight: ".5em",
						justifyContent: "space-between",
						height: "3rem",
					}}
				>
					<h4
						style={{
							color: "#F0F2F7",
							marginBottom: 0,
							alignSelf: "center",
						}}
					>
						{this.props.heading}
					</h4>

					<div
						className="d-none d-md-flex"
						style={{
							display: "flex",
							flexDirection: "row",
							margin: "auto 0",
						}}
					>
						{canTry && (
							<>
								{!isLoggedIn ? (
									<Tooltip placement="top" overlay="Login to try it">
										<Link to="/login/">
											<TryItIcon
												style={{
													marginBottom: 0,
													marginTop: "1px",
													cursor: "pointer",
												}}
												fillColor="rgba(0, 0, 0, 0.08)"
												iconColor="#FFFFFF"
											/>
										</Link>
									</Tooltip>
								) : (
									<Tooltip placement="top" overlay="Try it!">
										<TryItIcon
											style={{
												marginBottom: 0,
												marginTop: "1px",
												cursor: "pointer",
											}}
											fillColor="rgba(0, 0, 0, 0.08)"
											iconColor="#FFFFFF"
											onClick={this.openInLab.bind(this)}
										/>
									</Tooltip>
								)}
							</>
						)}

						{this.props.copytoclipboard === "false"
							? null
							: this.renderCopyToClipboard(this.exampleText)}
					</div>
				</div>

				<div className="gatsby-highlight">
					{this.exampleText === "" ? (
						<IntentionallyBlank />
					) : (
						<pre className={"language-" + this.props.language} style={{ marginTop: 0 }}>
							<code dangerouslySetInnerHTML={{ __html: markup }} />
						</pre>
					)}

					{this.state.responseBody === undefined ? null : (
						<pre
							style={{
								backgroundColor: "black",
								color: "white",
								paddingTop: 5,
								paddingBottom: 5,
								paddingLeft: 10,
								paddingRight: 10,
							}}
						>
							{this.state.responseBody}
						</pre>
					)}
				</div>
			</div>
		);
	}
}

const ExampleContainer = (props: any) => (
	<>
		<WithHeaders appId={props.appId} httpVerb={props.apiverb} authType={props.apiauthtype}>
			{headers => <Example {...props} headers={headers} />}
		</WithHeaders>
	</>
);

const mapStateToProps = state => ({
	isLoggedIn: userSelectors.selectIsLoggedIn(state),
	appId: userSelectors.selectAppId(state),
});

export default connect(mapStateToProps)(ExampleContainer);



// WEBPACK FOOTER //
// ./src/components/Example/Example.tsx